import React, { useMemo, useRef } from "react";
import { ExternalLink, FileDown } from "lucide-react";
import { Link } from "react-router-dom";
import { useLang } from "@/context/LanguageContext";
import { fmtNumber } from "@/lib/i18n";
import PanelHeader from "@/components/PanelHeader";
import PanelNav from "@/components/PanelNav";
import AdSlot from "@/components/AdSlot";
import ShareButtons from "@/components/ShareButtons";
import TravelGuide from "@/components/TravelGuide";
import CountryReviews from "@/components/CountryReviews";
import SongPlayer from "@/components/SongPlayer";
import { Stat, Section, Demographics, History, Borders } from "@/components/PanelSections";
import { useSwipeToClose } from "@/lib/useSwipeToClose";

const sortedCodes = (countries, lang) => {
  const key = lang === "ar" ? "ar" : "en";
  return [...countries]
    .sort((a, b) => (a.short_names[key] || a.short_names.en).localeCompare(b.short_names[key] || b.short_names.en, key))
    .map((c) => c.code);
};

function StatsGrid({ country }) {
  const { lang, t } = useLang();
  return (
    <div className="px-6 py-5 grid grid-cols-2 gap-4">
      <Stat label={t("area")} value={`${fmtNumber(country.area_km2, lang)} ${t("km2")}`} testid="stat-area" />
      <Stat label={t("population")} value={fmtNumber(country.population, lang)} testid="stat-pop" />
      <Stat label={t("currency")} value={country.currency[lang]} small testid="stat-currency" />
      <Stat label={t("gov_type")} value={country.gov_type[lang]} small testid="stat-gov" />
    </div>
  );
}

function PanelLinks({ country }) {
  const { lang, t } = useLang();
  return (
    <div className="px-6 pb-4 space-y-3">
      <div>
        <div className="text-[10px] uppercase tracking-[0.18em] text-ochre mb-2 font-semibold">{t("share")}</div>
        <ShareButtons country={country} />
      </div>
      <a href={`https://${lang}.wikipedia.org/wiki/${encodeURIComponent(country.names[lang])}`} target="_blank" rel="noopener noreferrer"
        className="inline-flex items-center gap-1.5 text-sm text-ochre hover:underline" data-testid="wiki-link">
        Wikipedia <ExternalLink className="w-3.5 h-3.5" />
      </a>
      {country.travel_guide && (
        <Link to={`/guide/${country.code}`} data-testid="download-guide-link"
          className="flex items-center justify-center gap-2 w-full py-2.5 rounded-xl border border-stoneLine bg-sand/60 hover:bg-ochre hover:text-white hover:border-ochre transition-colors text-sm font-medium">
          <FileDown className="w-4 h-4" /> {t("download_guide")}
        </Link>
      )}
    </div>
  );
}

export default function CountryPanel({ country, onClose, onNavigate, countries }) {
  const { lang, t, isRtl } = useLang();
  const sheetRef = useRef(null);
  useSwipeToClose(sheetRef, onClose, country?.code);

  // Compute prev/next locally — no API roundtrip.
  const neighbors = useMemo(() => {
    if (!country || countries.length === 0) return null;
    const codes = sortedCodes(countries, lang);
    const i = codes.indexOf(country.code);
    if (i === -1) return null;
    return { prev: codes[(i - 1 + codes.length) % codes.length], next: codes[(i + 1) % codes.length] };
  }, [country, countries, lang]);

  const codeToName = useMemo(
    () => Object.fromEntries(countries.map((c) => [c.code, c.short_names[lang] || c.short_names.en])),
    [countries, lang],
  );

  if (!country) return null;

  return (
    <>
      <div className="fixed inset-0 z-40 bg-ink/30 md:hidden animate-fade-in" onClick={onClose} data-testid="panel-backdrop" />
      <aside
        key={country.code}
        ref={sheetRef}
        className={`fixed z-50 bg-card border-stoneLine animate-fade-in
          bottom-0 left-0 right-0 h-[85vh] rounded-t-3xl border-t
          md:inset-y-0 md:top-0 md:h-full md:w-[440px] md:rounded-none md:border-t-0
          ${isRtl ? "md:right-0 md:left-auto md:border-e" : "md:left-auto md:right-0 md:border-s"}
          overflow-y-auto thin-scroll`}
        data-testid={`country-panel-${country.code}`}
      >
        <div className="md:hidden sticky top-0 z-10 bg-card pt-2 pb-1 flex justify-center" data-testid="sheet-handle">
          <div className="w-10 h-1.5 rounded-full bg-stoneLine" />
        </div>

        <PanelHeader country={country} onClose={onClose} />
        <SongPlayer song={country.song} modern={country.song_modern} code={country.code} />
        <StatsGrid country={country} />

        <Section title={t("intro")}>
          <p className="text-sm leading-relaxed text-ink/85" data-testid="panel-intro">{country.intro[lang]}</p>
        </Section>
        <Section title={t("economy")}>
          <p className="text-sm leading-relaxed text-ink/85" data-testid="panel-economy">{country.economy_base[lang]}</p>
        </Section>
        <Section title={t("nature")}>
          <ul className="space-y-2 text-sm">
            {["fauna", "mountains", "rivers"].map((k) => (
              <li key={k}><span className="text-xs uppercase tracking-widest text-muted-foreground me-2">{t(k)}</span>{country.nature[k][lang]}</li>
            ))}
          </ul>
        </Section>

        <Demographics country={country} />
        <History country={country} />

        {country.travel_guide && (
          <Section title={t("guide")}>
            <TravelGuide guide={country.travel_guide} code={country.code} />
          </Section>
        )}

        <Borders country={country} codeToName={codeToName} onNavigate={onNavigate} />

        <Section title={t("reviews")}>
          <CountryReviews code={country.code} country={country} />
        </Section>

        <PanelLinks country={country} />

        <div className="px-6 pb-4">
          <AdSlot placement="panel" />
        </div>

        <PanelNav neighbors={neighbors} onNavigate={onNavigate} />
      </aside>
    </>
  );
}
